import React, { useId, useRef, useState } from 'react';

import { Checkbox } from './ui/checkbox';
import { Skeleton } from './ui/skeleton';
import { FieldGroup } from './Field';
import { SearchInput, hasListError, isListRetrying } from './DataTable';
import { ErrorState } from './ErrorState';
import { ListContinuation } from './ListContinuation';
import type { ListStatus } from '../hooks/useInfiniteList';
import { formatNumber } from '../lib/formatters';
import { EYEBROW, cn } from '../lib/utils';

/** A filter appears once the set holds more than this many items. */
export const SEARCHABLE_FROM = 1;

export interface CheckboxListItem {
  id: string;
  /** The checkbox's accessible name. */
  label: string;
  /** Mono and muted, beside the label and part of its name (a slug, an alias). */
  meta?: string;
  /** Under the label, linked by aria-describedby — never part of the name. */
  description?: React.ReactNode;
  /**
   * Why this item cannot be chosen. The checkbox is disabled only while it is
   * unchecked, so a selection that became invalid can still be removed.
   */
  disabledReason?: string;
  /** A mark beside the label, e.g. `<StatusBadge status="new" />` for a pinned nested create. */
  adornment?: React.ReactNode;
  /** Extra words the in-memory filter matches when `description` is not plain text. */
  searchText?: string;
}

export interface CheckboxListSection {
  key: string;
  heading: string;
  items: CheckboxListItem[];
}

/** What a list that loads in batches passes; `checkboxListProps(list)` builds it from useInfiniteList. */
export interface CheckboxListBatchProps {
  /** The search box's value. With `onSearchChange` the search runs on the server. */
  searchInput?: string;
  onSearchChange?: (value: string) => void;
  /** The applied search, for the no-match copy. */
  search?: string;
  listStatus?: ListStatus;
  hasMore?: boolean;
  onLoadMore?: (options?: { auto?: boolean }) => void;
  onRetryBatch?: () => void;
  onReset?: () => void;
  /** A failed batch's error: its kind decides the inline Retry. */
  batchError?: unknown;
  /** The SERVER's total for the whole set — m in "{n} of {m} selected". */
  totalCount?: number | null;
  /** Rows loaded from the server (pins excluded), for the trailing controls. */
  loadedCount?: number;
  isLoading?: boolean;
  loadError?: unknown;
  onRetry?: () => void;
  isRetrying?: boolean;
}

export interface CheckboxListProps extends CheckboxListBatchProps {
  legend: string;
  /** Under the legend, above the list. */
  description?: React.ReactNode;
  /** Under the list; replaced by `error`. */
  help?: React.ReactNode;
  /** The group's validation message (e.g. "Select at least one role."). */
  error?: string | null;
  required?: boolean;
  /** Controls beside the legend. */
  actions?: React.ReactNode;
  /** Pass `items` or `sections`, not both. */
  items?: CheckboxListItem[];
  sections?: CheckboxListSection[];
  selectedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  disabled?: boolean;
  /** Plural noun: 'templates', 'event types', 'lenses', 'roles'. */
  itemNoun: string;
  searchPlaceholder?: string;
  /** Overrides the SEARCHABLE_FROM rule (specimens and tests). */
  searchable?: boolean;
  /** Checkbox ids are `{idPrefix}-{item.id}`; generated when absent. */
  idPrefix?: string;
  /** ErrorState title for a failed load. Defaults to "Couldn't load {itemNoun}". */
  loadErrorTitle?: string;
  /** Shown when the set is empty and nothing is searched. A string is drawn in the kit's dashed box. */
  emptyMessage?: React.ReactNode;
  className?: string;
}

/** The slice of a useInfiniteList result a picker reads. */
interface BatchSource {
  items: readonly unknown[];
  status: ListStatus;
  error: unknown;
  hasMore: boolean;
  totalCount: number | null;
  loadMore: (options?: { auto?: boolean }) => void;
  retryBatch: () => void;
  reset: () => void;
  searchInput: string;
  setSearchInput: (value: string) => void;
  search: string;
}

/** Mirrors DataTable's listTableProps: spread a picker's useInfiniteList into CheckboxList. */
export function checkboxListProps(list: BatchSource): CheckboxListBatchProps {
  return {
    searchInput: list.searchInput,
    onSearchChange: list.setSearchInput,
    search: list.search,
    listStatus: list.status,
    hasMore: list.hasMore,
    onLoadMore: list.loadMore,
    onRetryBatch: list.retryBatch,
    onReset: list.reset,
    batchError: list.status === 'appendError' ? list.error : undefined,
    totalCount: list.totalCount,
    loadedCount: list.items.length,
    isLoading: list.status === 'initial' && list.error == null,
    loadError: hasListError(list) ? list.error : undefined,
    onRetry: list.reset,
    isRetrying: isListRetrying(list),
  };
}

const plainText = (value: React.ReactNode): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

/**
 * Choosing a set inside a form or dialog (DESIGN_SYSTEM §4, §5 "Choosing
 * several items").
 *
 * - a fieldset named by its legend, with FieldGroup's required mark and linked help/error
 * - "{n} of {m} selected · {k} shown" on the legend row, where m is the whole set
 * - a filter once the set holds more than SEARCHABLE_FROM items; on the server
 *   when the list loads in batches. Filtering narrows what is drawn, never
 *   what is selected
 * - sections with headings, blocked items with a reason, Skeleton loading,
 *   an error with Retry, empty and no-match copy
 * - for a list that loads in batches, the shared trailing controls (Load more,
 *   an inline Retry for a failed batch) inside the scroller
 */
export const CheckboxList: React.FC<CheckboxListProps> = ({
  legend,
  description,
  help,
  error,
  required,
  actions,
  items,
  sections,
  selectedIds,
  onToggle,
  disabled = false,
  itemNoun,
  searchPlaceholder,
  searchable,
  idPrefix,
  loadErrorTitle,
  emptyMessage,
  className,
  searchInput,
  onSearchChange,
  search,
  listStatus,
  hasMore = false,
  onLoadMore,
  onRetryBatch,
  onReset,
  batchError,
  totalCount,
  loadedCount,
  isLoading = false,
  loadError,
  onRetry,
  isRetrying,
}) => {
  const generatedPrefix = useId();
  const prefix = idPrefix ?? generatedPrefix;
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [localSearch, setLocalSearch] = useState('');

  const serverSearch = Boolean(onSearchChange);
  const activeSearch = (serverSearch ? search ?? '' : localSearch).trim();
  const typedSearch = serverSearch ? searchInput ?? '' : localSearch;

  const groups: CheckboxListSection[] = sections ?? [{ key: 'items', heading: '', items: items ?? [] }];
  const allCount = groups.reduce((sum, group) => sum + group.items.length, 0);

  const matches = (item: CheckboxListItem): boolean => {
    if (serverSearch || !activeSearch) return true;
    const haystack = [item.label, item.meta, plainText(item.description), item.searchText]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(activeSearch.toLowerCase());
  };
  const drawn = groups.map((group) => ({ ...group, items: group.items.filter(matches) }));
  const shownCount = drawn.reduce((sum, group) => sum + group.items.length, 0);

  /*
   * m is the whole set. Under a server search the list's totalCount becomes
   * the search's total, so the last total seen without a search is kept; it is
   * forgotten when the list starts over (an organization switch).
   */
  const knownTotal = useRef<number | null>(null);
  if (listStatus === 'initial') knownTotal.current = null;
  if (listStatus && !activeSearch && typeof totalCount === 'number') knownTotal.current = totalCount;
  const wholeSet = listStatus ? knownTotal.current : allCount;

  const selectedCount = selectedIds.size;
  const count =
    isLoading || loadError != null
      ? null
      : wholeSet != null
        ? `${formatNumber(selectedCount)} of ${formatNumber(wholeSet)} selected · ${formatNumber(shownCount)} shown`
        : `${formatNumber(selectedCount)} selected · ${formatNumber(shownCount)} shown`;

  const showFilter =
    searchable ??
    (typedSearch !== '' || (listStatus ? wholeSet == null || wholeSet > SEARCHABLE_FROM : allCount > SEARCHABLE_FROM));

  const renderItem = (item: CheckboxListItem) => {
    const checked = selectedIds.has(item.id);
    const inputId = `${prefix}-${item.id}`;
    const descriptionId = item.description ? `${inputId}-description` : null;
    const reasonId = item.disabledReason ? `${inputId}-reason` : null;
    const blocked = Boolean(item.disabledReason) && !checked;
    const inert = disabled || blocked;
    return (
      <li
        key={item.id}
        data-checkbox-item={item.id}
        className="flex items-start gap-2 rounded-md p-2 text-sm hover:bg-muted/40"
      >
        <Checkbox
          id={inputId}
          className="mt-0.5"
          checked={checked}
          disabled={inert}
          onCheckedChange={() => onToggle(item.id)}
          aria-describedby={[descriptionId, reasonId].filter(Boolean).join(' ') || undefined}
        />
        <div className="min-w-0 flex-1">
          <label
            htmlFor={inputId}
            className={cn('flex flex-wrap items-center gap-x-2 gap-y-0.5', inert ? 'cursor-not-allowed' : 'cursor-pointer')}
          >
            <span className="font-medium leading-tight">{item.label}</span>
            {item.meta ? <span className="font-mono text-xs text-muted-foreground">{item.meta}</span> : null}
            {item.adornment}
          </label>
          {item.description ? (
            <div id={descriptionId ?? undefined} className="mt-0.5 text-xs text-muted-foreground">
              {item.description}
            </div>
          ) : null}
          {item.disabledReason ? (
            <p id={reasonId ?? undefined} className="mt-0.5 text-xs text-warning-emphasis">
              {item.disabledReason}
            </p>
          ) : null}
        </div>
      </li>
    );
  };

  let body: React.ReactNode;
  if (loadError != null) {
    body = (
      <ErrorState
        compact
        title={loadErrorTitle ?? `Couldn't load ${itemNoun}`}
        error={loadError}
        onRetry={onRetry}
        isRetrying={isRetrying}
      />
    );
  } else if (isLoading) {
    body = (
      <div className="space-y-2 rounded-md border p-3" aria-busy="true">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-3/5" />
      </div>
    );
  } else if (shownCount === 0 && activeSearch) {
    body = (
      <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
        {`No ${itemNoun} match "${activeSearch}".`}
        {selectedCount > 0 ? ' Your selections are unaffected.' : ''}
      </p>
    );
  } else if (shownCount === 0) {
    body =
      emptyMessage == null || typeof emptyMessage === 'string' ? (
        <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
          {emptyMessage ?? `No ${itemNoun} available.`}
        </p>
      ) : (
        emptyMessage
      );
  } else {
    body = (
      <div
        ref={scrollerRef}
        data-checkbox-list=""
        aria-busy={listStatus === 'refreshing' || undefined}
        className="max-h-72 space-y-2 overflow-y-auto rounded-md border p-1"
      >
        {drawn.map((group) =>
          group.items.length === 0 ? null : (
            <div key={group.key} className="space-y-0.5">
              {group.heading ? <p className={cn(EYEBROW, 'px-2 pt-1')}>{group.heading}</p> : null}
              <ul className="space-y-0.5">{group.items.map(renderItem)}</ul>
            </div>
          ),
        )}
        {listStatus ? (
          <ListContinuation
            status={listStatus}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            onRetryBatch={onRetryBatch}
            onReset={onReset}
            error={batchError}
            itemNoun={itemNoun}
            loadedCount={loadedCount ?? shownCount}
            totalCount={totalCount}
            rootRef={scrollerRef}
          />
        ) : null}
      </div>
    );
  }

  return (
    <FieldGroup
      legend={legend}
      description={description}
      help={help}
      error={error}
      required={required}
      actions={actions}
      aside={count}
      className={className}
    >
      {showFilter && loadError == null ? (
        // The list usually sits inside a <form>: Enter in the filter must never submit it.
        <div
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.preventDefault();
          }}
        >
          <SearchInput
            value={typedSearch}
            onChange={serverSearch ? (onSearchChange as (value: string) => void) : setLocalSearch}
            noun={itemNoun}
            placeholder={searchPlaceholder ?? `Search ${itemNoun}…`}
            fill
          />
        </div>
      ) : null}
      {body}
    </FieldGroup>
  );
};
